import { useEffect, useMemo, useState } from 'react';
import { TableColumnType } from '../Table';
import { CardFilterState, CardSortState } from './TableCardViewControls';
import { getColumnKey, isFilterable, isSortable } from './tableCardViewUtils';

function getDefaultSort<T>(columns: TableColumnType<T>[]): CardSortState {
  const defaultColumn = columns.find(
    (column) =>
      column.defaultSortOrder === 'ascend' ||
      column.defaultSortOrder === 'descend',
  );

  if (!defaultColumn) return {};

  return {
    key: String(defaultColumn.key),
    order: defaultColumn.defaultSortOrder,
  };
}

export function useTableCardView<T extends object>(
  columns: TableColumnType<T>[],
  dataSource: readonly T[] | undefined,
) {
  const sortableColumns = useMemo(() => columns.filter(isSortable), [columns]);
  const filterableColumns = useMemo(
    () => columns.filter(isFilterable),
    [columns],
  );
  const defaultSort = useMemo(
    () => getDefaultSort(sortableColumns),
    [sortableColumns],
  );
  const [sort, setSort] = useState<CardSortState>(defaultSort);
  const [filters, setFilters] = useState<CardFilterState>({});

  useEffect(() => {
    setSort((currentSort) =>
      currentSort.key &&
      sortableColumns.some((column) => String(column.key) === currentSort.key)
        ? currentSort
        : defaultSort,
    );
  }, [defaultSort, sortableColumns]);

  const displayData = useMemo(() => {
    let data = [...(dataSource ?? [])];

    const activeFilters = filterableColumns
      .map((column, index) => ({
        column,
        value: filters[getColumnKey(column, index)]?.trim(),
      }))
      .filter((x) => !!x.value);

    if (activeFilters.length > 0) {
      data = data.filter((record) =>
        activeFilters.every(({ column, value }) =>
          column.onFilter!(value as string, record),
        ),
      );
    }

    if (sort.key && sort.order) {
      const sortColumn = sortableColumns.find(
        (column, index) => getColumnKey(column, index) === sort.key,
      );
      const comparator = sortColumn?.sorter;
      if (typeof comparator === 'function') {
        data.sort((a, b) => (comparator as (a: T, b: T) => number)(a, b));
        if (sort.order === 'descend') {
          data.reverse();
        }
      }
    }

    return data;
  }, [dataSource, filters, sort, filterableColumns, sortableColumns]);

  return {
    sortableColumns,
    filterableColumns,
    sort,
    setSort,
    filters,
    setFilters,
    displayData,
  };
}
